import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import type { Course } from '@cvs/shared';
import { api, setCourseIdProvider } from './api';
import { useAuth } from './auth';

/** Reserved slug meaning "show every course merged" (instructors only). */
export const ALL_COURSES = 'all';
const STORAGE_KEY = 'cvs.courseSlug';

/**
 * Course selection is driven by the URL: student content lives under
 * `/courses/:slug/…` (and the Studio content editor under
 * `/studio/content/:slug`). The slug in the path is the single source of truth —
 * switching course is just navigation, so links are bookmarkable and a refresh
 * keeps you in the same course. The api client reads the resolved course id at
 * fetch time so requests are scoped correctly.
 */
interface CourseCtx {
  courses: Course[];
  loading: boolean;
  /** Slug from the URL (or ALL_COURSES), or null when not on a course-scoped route. */
  currentSlug: string | null;
  /** Resolved course for the current slug (null for ALL_COURSES or when off-course). */
  currentCourse: Course | null;
  /** Build a path inside the active course: coursePath('labs') → /courses/<slug>/labs. */
  coursePath: (sub?: string) => string;
  /** Navigate to a course by id (ALL_COURSES for the merged view), or to the picker (null). */
  goToCourse: (courseId: string | null) => void;
  /** Overall completion % for the current course (null when unknown / all courses). */
  courseProgress: number | null;
}

const CourseContext = createContext<CourseCtx | null>(null);

function readSlug(pathname: string): string | null {
  const m =
    matchPath('/courses/:slug/*', pathname) ??
    matchPath('/courses/:slug', pathname) ??
    matchPath('/studio/content/:slug', pathname);
  const slug = m?.params?.slug;
  return slug && slug !== 'unfiled' ? slug : null;
}

export function CourseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [courseProgress, setCourseProgress] = useState<number | null>(null);

  const currentSlug = readSlug(pathname);
  const currentCourse =
    currentSlug && currentSlug !== ALL_COURSES ? courses.find((c) => c.slug === currentSlug) ?? null : null;

  // Feed the api client the scoped course id at fetch time. ALL_COURSES / off-course → null (unscoped).
  const courseId = currentSlug === ALL_COURSES ? null : currentCourse?.id ?? null;
  const courseIdRef = useRef<string | null>(courseId);
  courseIdRef.current = courseId;
  const injected = useRef(false);
  if (!injected.current) {
    injected.current = true;
    setCourseIdProvider(() => courseIdRef.current);
  }

  // Remember the last course you were on (drives the '/' redirect and re-login landing).
  useEffect(() => {
    if (currentSlug) localStorage.setItem(STORAGE_KEY, currentSlug);
  }, [currentSlug]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setCourses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .courses()
      .then((cs) => {
        if (!cancelled) {
          setCourses(cs);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.role]);

  // Overall completion % for the current course — feeds the sidebar course block.
  useEffect(() => {
    const cid = currentSlug === ALL_COURSES ? null : currentCourse?.id ?? null;
    if (!user || !cid) {
      setCourseProgress(null);
      return;
    }
    let cancelled = false;
    setCourseProgress(null);
    api
      .program()
      .then((p) => {
        if (cancelled) return;
        const done = p.modules.reduce((s, m) => s + m.completedLessons, 0);
        const total = p.modules.reduce((s, m) => s + m.totalLessons, 0);
        setCourseProgress(total ? Math.round((done / total) * 100) : 0);
      })
      .catch(() => {
        if (!cancelled) setCourseProgress(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentCourse?.id, currentSlug, user?.id]);

  // A sensible slug for building nav links even when you're on the picker or a
  // global page: the current course, else your last one, else your first.
  const fallbackSlug = useMemo(() => {
    if (currentSlug) return currentSlug;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (stored === ALL_COURSES || courses.some((c) => c.slug === stored))) return stored;
    return courses[0]?.slug ?? ALL_COURSES;
  }, [currentSlug, courses]);

  const coursePath = useCallback(
    (sub = '') => (sub ? `/courses/${fallbackSlug}/${sub}` : `/courses/${fallbackSlug}`),
    [fallbackSlug],
  );

  const goToCourse = useCallback(
    (id: string | null) => {
      if (!id) return navigate('/courses');
      const slug = id === ALL_COURSES ? ALL_COURSES : courses.find((c) => c.id === id)?.slug;
      // Land on the course home (its curriculum), not the dashboard.
      navigate(slug ? `/courses/${slug}` : '/courses');
    },
    [courses, navigate],
  );

  const value = useMemo(
    () => ({ courses, loading, currentSlug, currentCourse, coursePath, goToCourse, courseProgress }),
    [courses, loading, currentSlug, currentCourse, coursePath, goToCourse, courseProgress],
  );

  return <CourseContext.Provider value={value}>{children}</CourseContext.Provider>;
}

export function useCourse(): CourseCtx {
  const c = useContext(CourseContext);
  if (!c) throw new Error('useCourse must be used within CourseProvider');
  return c;
}

/** Where '/' should send you: your only course, your last course, or the picker.
 *  Lands on the course home (its curriculum). */
export function courseHomePath(courses: Course[]): string {
  if (courses.length === 1) return `/courses/${courses[0].slug}`;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (stored === ALL_COURSES || courses.some((c) => c.slug === stored))) return `/courses/${stored}`;
  return '/courses';
}
